import { useEffect, useMemo, useState } from "react"

import localStore from "@/components/localStore"
import { useGetOnboardingSetupJourneyQuery } from "@/store/onboardingAPI"
import { useGetMeQuery } from "@/store/userAPI"

const BASE_KEY = "setup_acknowledged"
const ACKNOWLEDGE_EVENT = "moogli:setup-acknowledged"

function getScopedKey(userId: string | undefined) {
  return userId ? `${userId}_${BASE_KEY}` : BASE_KEY
}

function isDismissed(scopedKey: string) {
  return localStore.getItem("onboardingDismissed")[scopedKey] === true
}

function setDismissed(scopedKey: string) {
  const current = localStore.getItem("onboardingDismissed")
  localStore.setItem("onboardingDismissed", { ...current, [scopedKey]: true })
}

/**
 * Returns whether Scope 1 (and the intentional Climate overview) should
 * be blocked for the current user.
 *
 * Blocked while:
 *  - API is loading (prevents content flash)
 *  - setup is not yet complete
 *  - setup IS complete but the user has not yet acknowledged the completion screen
 *
 * `shouldShowOnboardingBanners` is true as long as setup is not fully
 * acknowledged — use this to gate per-page onboarding banners.
 *
 * `acknowledge` persists to localStore scoped by userId so it survives
 * page refreshes and new sessions, while avoiding cross-account bleed.
 */
export function useScope1Blocked() {
  const { data, isLoading } = useGetOnboardingSetupJourneyQuery()
  const { data: meData } = useGetMeQuery()
  const userId = meData?.user.id

  const storageKey = useMemo(() => getScopedKey(userId), [userId])

  const [acknowledged, setAcknowledged] = useState(false)

  // Sync from localStore once userId (and therefore the scoped key) is resolved.
  useEffect(() => {
    setAcknowledged(isDismissed(storageKey))
  }, [storageKey])

  // React to acknowledgments triggered elsewhere in the same tab.
  useEffect(() => {
    const handler = () => setAcknowledged(true)
    window.addEventListener(ACKNOWLEDGE_EVENT, handler)
    return () => window.removeEventListener(ACKNOWLEDGE_EVENT, handler)
  }, [])

  const setupComplete = data?.setupComplete ?? false

  const acknowledge = () => {
    setDismissed(storageKey)
    window.dispatchEvent(new CustomEvent(ACKNOWLEDGE_EVENT))
  }

  return {
    // Block until loaded + complete + user has seen/dismissed the completion screen
    isBlocked: isLoading || !setupComplete || !acknowledged,
    isLoading,
    setupComplete,
    // False once the user has fully acknowledged setup — hides per-page banners
    shouldShowOnboardingBanners: !setupComplete || !acknowledged,
    acknowledge,
  }
}
